import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	pickCalendarBootstrap,
	pickCalendarFilters,
	pickCalendarRefs,
	pickCalendarEvents
} from '../../calendar-common/viewStoreKeys.js';
import { useSelector, shallowEqual } from 'react-redux';
import { selectWeightUnit } from '../selectors/selectWeightUnit.js';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Chip,
	Spinner,
	Divider,
	Dropdown,
	DropdownTrigger,
	DropdownMenu,
	DropdownItem,
	addToast
} from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
	Plane,
	Wrench,
	ExternalLink,
	ChevronDown,
	Layers,
	CheckCircle,
	XCircle
} from 'lucide-react';
import CalendarApi from '../../../api/CalendarApi.js';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';
import ResourcesApi from '../../../api/resourcesApi.js';
import CounterApi from '../../../api/CounterApi.js';
import { buildCustomFieldRows } from '../utils/eventCustomFieldRows.js';
import { pickInstructorTotal } from '../utils/instructorTotal.js';
import WeightBalanceScreen from './WeightBalanceScreen.js';
import ResourcePanel from './ResourcePanel.js';
import DiscrepancyView from './DiscrepancyView.js';
import { unwrap } from '../utils/apiEnvelope.js';
import EventFormBody from './EventFormBody.js';
import CreateEventModal from './CreateEventModal.js';
import CancelEventConfirm from './CancelEventConfirm.js';
import RecurringScopeConfirm, {
	EDIT_RECURRING_OPTIONS,
	DELETE_RECURRING_OPTIONS
} from './RecurringScopeConfirm.js';
import { normalizeDetailEvent } from '../utils/normalizeDetailEvent.js';
import { mapEventToFormState } from '../utils/mapEventToFormState.js';
import { formatRecurrenceSummary } from '../utils/recurrenceSummary.js';
import { LocationLabel } from '../utils/formatLocationLabel.js';
import { getMaintenanceResourceId } from '../utils/getMaintenanceResourceId.js';
import { isSubmittedMyEvent } from '../utils/isSubmittedMyEvent.js';
import { getEventOrgPerms } from '../utils/getEventOrgPerms.js';
import { getAirworthinessStatusFromInspections } from '../../resources/utils/inspectionStatus.js';
import { computeEventPermissions } from '../utils/eventPermissions.js';
import CheckOutScreen from './CheckOutScreen.js';
import CheckInScreen from './CheckInScreen.js';
import {
	formatDateTime,
	formatDateTimeWithOffset,
	computeOffsetString
} from '../utils/timeFormatters.js';
import useBreakpoint from '../../calendar-common/hooks/useBreakpoint.js';

// Rem20 Sub 7c — Invoice flow is being rewritten; hide UI affordances
// (View Invoice + Create Invoice) until ready. Flip to `true` (or remove
// the gate) when the new invoice flow lands. `canCreateInvoice` /
// `canViewInvoice` in `eventPermissions.js` stay computed correctly so
// the restore is a single-line change.
const SHOW_INVOICE_AFFORDANCES = false;
const EMPTY_ACTIVITY_TYPES = {};

/**
 * Flatten the single-event response (Event entity with nested User/Location/
 * Organization/Resource objects) into the flat shape EventContent renders.
 * Listing endpoint (`/calendar/events/{view}`) already returns flat fields,
 * so the fallbacks preserve those.
 */
// Symfony serializes EventResources two ways depending on the legacy → modern
// data path. Aircraft / simulator may live as a nested sub-object on the
// EventResource OR on its `resource`, OR the `resource` itself may be flat
// with `type === 'simulator' | 'aircraft'` and the entity's fields inlined.
// Returns the aviation entity (or null) keeping `id` / `name` /
// `registrationNumber` access stable regardless of shape.
function pickAircraft(er) {
	return (
		er?.aircraft ||
		er?.resource?.aircraft ||
		(er?.resource && String(er.resource?.type) === 'aircraft' ? er.resource : null) ||
		null
	);
}
function pickSimulator(er) {
	return (
		er?.simulator ||
		er?.resource?.simulator ||
		(er?.resource && String(er.resource?.type) === 'simulator' ? er.resource : null) ||
		null
	);
}

function normalizeEventDetail(raw) {
	if (!raw || typeof raw !== 'object') return raw;
	const pilot = raw.pilot || {};
	const coPilot = raw.coPilot || {};
	const instructor = raw.instructor || {};
	const location = raw.location || {};
	const org = raw.organization || {};
	// Symfony serializes recurrence as a nested `recurringEvent` (FK to
	// `EventsSet`) — the Event entity itself has NO flat `recurring` /
	// `recurringType` / `reps` / etc. properties. Translate the nested
	// object into flat keys here so `recurrenceSummary` and
	// `mapEventToFormState` (both reading flat keys) work uniformly.
	// Field name nuances:
	//   - backend `recurrence` (count of reps)  → frontend `reps`
	//   - backend `endTime`    (unix seconds)   → frontend `recurringEndDate` (YYYY-MM-DD)
	const recurringEvent = raw.recurringEvent || null;
	const recurringEndDateFromTs = recurringEvent?.endTime
		? new Date(Number(recurringEvent.endTime) * 1000).toISOString().slice(0, 10)
		: null;

	const resources = Array.isArray(raw.eventResources) ? raw.eventResources : [];
	let resourceName = raw.resourceName || '';
	let registrationNumber = raw.registrationNumber || raw.regNumb || '';
	for (const er of resources) {
		const res = er?.resource;
		if (!res) continue;
		if (!resourceName) resourceName = res.name || '';
		const aircraft = res.aircraft;
		if (!registrationNumber && aircraft?.registrationNumber) {
			registrationNumber = aircraft.registrationNumber;
		}
		if (registrationNumber && resourceName) break;
	}

	return {
		...raw,
		pilotId: raw.pilotId ?? pilot.id ?? null,
		coPilotId: raw.coPilotId ?? coPilot.id ?? null,
		instructorId: raw.instructorId ?? instructor.id ?? null,
		pilotName: raw.pilotName ?? pilot.name ?? '',
		pilotSurname: raw.pilotSurname ?? pilot.surname ?? '',
		coPilotName: raw.coPilotName ?? coPilot.name ?? '',
		coPilotSurname: raw.coPilotSurname ?? coPilot.surname ?? '',
		instructorName: raw.instructorName ?? instructor.name ?? '',
		instructorSurname: raw.instructorSurname ?? instructor.surname ?? '',
		locationName: raw.locationName ?? location.name ?? '',
		locationTimezoneId: raw.locationTimezoneId ?? location.timeZoneId ?? '',
		organizationName: raw.organizationName ?? org.name ?? '',
		organizationUnique: raw.organizationUnique ?? org.unique ?? '',
		resourceName,
		registrationNumber,
		// Recurring flat-key derivations from nested `recurringEvent`.
		recurring: raw.recurring ?? recurringEvent != null,
		repsInterval: raw.repsInterval ?? recurringEvent?.repsInterval ?? 1,
		recurringType: raw.recurringType ?? recurringEvent?.recurringType ?? 'day',
		daysOfWeek: raw.daysOfWeek ?? recurringEvent?.daysOfWeek ?? [],
		repsType: raw.repsType ?? recurringEvent?.repsType ?? 'number',
		reps: raw.reps ?? recurringEvent?.recurrence ?? null,
		recurringEndDate: raw.recurringEndDate ?? recurringEndDateFromTs
	};
}

// Re-export from here so the existing `EventViewModal.formatters.test.js`
// keeps working without rewriting imports.
export { formatDateTime, formatDateTimeWithOffset, computeOffsetString };

/**
 * Resolve event type label and color.
 */
function resolveEventType(event) {
	if (!event) return { label: 'Event', color: 'default', isMaintenance: false };
	const t = (event.type || '').toLowerCase();
	if (t.includes('maint'))
		return { label: 'Maintenance Event', color: 'warning', isMaintenance: true };
	if (t.includes('sim'))
		return { label: 'Simulator Event', color: 'secondary', isMaintenance: false };
	return { label: 'Regular Event', color: 'primary', isMaintenance: false };
}

/**
 * Resolve event status — returns {label, color, phase} where `phase` drives
 * the inline action button (`checkout` / `checkin` / null).
 * HeroUI chip colors: primary|secondary|success|warning|danger|default.
 *
 * Listing DTO exposes derived flags (`inFlight`/`completed`/`reserve`);
 * single-event response serializes the Event entity which only carries
 * `checkOutTime`/`checkInTime` integers — derive the same states from either.
 */
function resolveStatus(event) {
	if (!event) {
		return {
			label: '',
			color: 'default',
			phase: null,
			checkOutEnable: false,
			checkInEnable: false,
			aircraftExists: false,
			checkOutError: '',
			positionInQueue: null
		};
	}
	const isTruthy = v => v === '1' || v === true || v === 1;
	const t = (event.type || '').toLowerCase();
	const typePrefix = t.includes('maint') ? 'Maintenance' : 'Regular';

	// `additionalData` parity (Phase 2.1 Sub 13). Symfony exposes the
	// authoritative button-enable flags + check-out error message + waiting-
	// list position in `event.additionalData` per
	// `EventAdditionalDataDto.php:25-41`. Older response shapes carry the
	// same fields at the top level — accept either for forwards-compat.
	const ad = event.additionalData || {};

	const completed = isTruthy(event.completed) || Number(event.checkInTime) > 0;
	const inFlight = !completed && (isTruthy(event.inFlight) || Number(event.checkOutTime) > 0);
	// Reserve (waiting-list) derivation. The month-list DTO carries a top-level
	// `reserve` bool, but the single-event detail DTO (`CalendarApi.getEvent`)
	// does NOT — it exposes the enum `EventResourceReserveStatus` under
	// `additionalData.reserve` (0=ACTIVE, 1=WAITING_LIST, 2=INSTRUCTOR). Fall
	// back to it so the modal hides the Check Out button on reserve events
	// (legacy parity). Only `WAITING_LIST` (1) counts as reserve — `INSTRUCTOR`
	// (2) is NOT a reserve.
	const reserve = isTruthy(event.reserve) || Number(ad.reserve) === 1;

	const checkOutEnable = isTruthy(ad.checkOutEnable ?? event.checkOutEnable);
	const checkInEnable = isTruthy(ad.checkInEnable ?? event.checkInEnable);
	// Rem20 Sub 7b — Symfony `EventAdditionalDataDto.php:31` ships
	// `aviationResourceExists` (current name); older response shapes use
	// `aircraftExists` (legacy name). Accept BOTH at top level AND nested
	// under `additionalData` so Check Out / Check In / Cancel Check Out /
	// Add to Waiting List buttons render regardless of which name the
	// backend currently sends. Symfony serializes the field as a boolean;
	// `isTruthy` already accepts `true` / `1` / `'1'`.
	const aircraftExists = isTruthy(
		ad.aviationResourceExists ??
			ad.aircraftExists ??
			event.aviationResourceExists ??
			event.aircraftExists
	);
	const checkOutError = String(ad.checkOutError ?? event.checkOutError ?? '').trim();
	const rawPosition = ad.positionInQueue ?? event.positionInQueue;
	const positionInQueue =
		rawPosition !== null && rawPosition !== undefined && rawPosition !== ''
			? Number(rawPosition)
			: null;

	let base;
	if (completed) {
		base = { label: `${typePrefix} Completed`, color: 'success', phase: null };
	} else if (inFlight) {
		base = { label: `${typePrefix} Checked Out`, color: 'primary', phase: 'checkin' };
	} else if (reserve) {
		base = { label: `${typePrefix} Reserve`, color: 'secondary', phase: null };
	} else {
		base = { label: `${typePrefix} Not Started`, color: 'warning', phase: 'checkout' };
	}

	return {
		...base,
		checkOutEnable,
		checkInEnable,
		aircraftExists,
		checkOutError,
		positionInQueue: Number.isFinite(positionInQueue) ? positionInQueue : null
	};
}

/** Label-value row for the detail grid. */
function Field({ label, children }) {
	if (!children) return null;
	return (
		<div className="mb-3">
			<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-0.5">
				{label}
			</p>
			<div className="text-sm text-default-900">{children}</div>
		</div>
	);
}

/**
 * EventViewModal — two-column event detail modal with in-place Edit screen
 * + Delete confirm + recurring scope dialog (Task 14, sub-epic 758f4ba2).
 *
 * Screen state machine: `screen='view' | 'edit'`. The Modal frame stays
 * mounted across the transition (no close→reopen flicker — legacy parity
 * with `calendar-day.event-modals.js:417`). Edit click swaps the inner body
 * to `<EventFormBody mode='edit'/>`; Cancel returns to view; Save success
 * closes the modal entirely.
 */
function EventViewModal({ isOpen, onClose, eventId, orgUnique, onUpdated, onDeleted }) {
	const [event, setEvent] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [screen, setScreen] = useState('view');
	const [selectedResource, setSelectedResource] = useState(null); // { id, type, label }
	const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
	const [previousScreen, setPreviousScreen] = useState('view'); // for back-nav from resource
	const [scopeDialog, setScopeDialog] = useState(null); // null | { kind: 'edit'|'delete', payload? }
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const [cancelCheckOutConfirm, setCancelCheckOutConfirm] = useState(false);
	const [cancelCheckInConfirm, setCancelCheckInConfirm] = useState(false);
	const [cancelDialog, setCancelDialog] = useState(false);
	const [waitingModal, setWaitingModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	// Rem21 Sub 10 — bump on Check Out / Check In submit + Cancel Check Out /
	// Cancel Check In success to trigger an event-detail re-fetch so the
	// returning view reflects the new status (buttons swap, status block
	// updates). Keeps the modal open instead of closing it.
	const [refetchKey, setRefetchKey] = useState(0);
	// Keyed by aircraftId: { count: N, airworthyStatus: 'unknown'|'airworthy'|'unairworthy' } — populated lazily after event loads
	const [resourceBadges, setResourceBadges] = useState({});
	// Incremented when a discrepancy is deleted from DiscrepancyView → remounts ResourcePanel
	const [resourcePanelKey, setResourcePanelKey] = useState(0);

	// With multi-org selected, the listing slice has events from multiple
	// orgs but the URL-path (and default `orgUnique` prop) points at only
	// one of them. Look up the specific event's own org so the detail fetch
	// hits the right scoped endpoint instead of 404-ing.
	const eventOrgUnique = useSelector(state => {
		if (!eventId) return null;
		const ev = pickCalendarEvents(state)?.[eventId];
		return ev?.organizationUnique || null;
	});

	const customFieldsMap = useSelector(state => state?.customFields);
	const resourcesMap = useSelector(state => state?.resources);
	const currentUserId = useSelector(
		state => state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);
	const timezone = useSelector(
		state => pickCalendarFilters(state)?.timezone || pickCalendarBootstrap(state)?.timezone || 'UTC'
	);
	const userSettings = useSelector(state => pickCalendarBootstrap(state)?.userSettings);
	// Cross-org perms resolver — null while the dashboard fan-out for this
	// org is still in flight (or failed). EventContent gates Edit/Delete
	// affordances on `perms.canDelete` / `perms.canEditEvent`.
	const eventOrgUniqueForPerms = event?.organizationUnique || eventOrgUnique || orgUnique;
	// `shallowEqual`: getEventOrgPerms BUILDS its flag object on every call, so
	// reference equality re-rendered the modal on every store tick and tripped
	// react-redux's unstable-selector warning (tester console).
	const perms = useSelector(state => getEventOrgPerms(state, eventOrgUniqueForPerms), shallowEqual);

	// Raw cross-org accessGroups map (current org + Phase-3 fan-out) used by
	// `computeEventPermissions` to read the legacy permission flag names
	// (`calendarManageMy`, `calendarEventDelete`, etc.). Differs from the
	// wrapped `perms` above (which renames flags via
	// `buildCalendarPermissionsForOrg`).
	// Same story as `perms`: the spread mints a new map per call; the VALUES
	// are stable store references, so shallowEqual is exactly the right depth.
	const accessGroupsMap = useSelector(state => {
		const bs = pickCalendarBootstrap(state);
		const currentOrgUnique = bs.organization?.unique || bs.orgUnique || null;
		const map = { ...(bs.eventOrgPerms || {}) };
		if (currentOrgUnique && bs.accessGroups) {
			map[currentOrgUnique] = bs.accessGroups;
		}
		return map;
	}, shallowEqual);
	// Per-button gating flag map (Phase 2.2 Sub 2). Single source of truth
	// for every footer + Status-block button visibility. `event === null` →
	// every flag false → no button rendered.
	const flags = useMemo(
		// Rem18 Task 2 — `resourcesMap` enables the instructor-ownership
		// branch in `computeEventPermissions` (instructor-assigned users
		// get the same gates as pilot owners when they have `calendarManageMy`).
		() => computeEventPermissions(event, accessGroupsMap, currentUserId, resourcesMap),
		[event, accessGroupsMap, currentUserId, resourcesMap]
	);

	// Reset screen + dialog state every time the modal opens / closes / event
	// changes so a stale "edit" screen doesn't re-appear when the user picks
	// a different event without saving. Rem21 Sub 9: also reset on close so
	// stale `screen='checkin'/'checkout'` doesn't survive into the next
	// open — the previous early-exit caused a spurious `check-in-start`
	// request on the first render of a fresh event when the prior session
	// closed mid-flow.
	useEffect(() => {
		setScreen('view');
		setSelectedResource(null);
		setSelectedDiscrepancy(null);
		setPreviousScreen('view');
		setScopeDialog(null);
		setDeleteConfirm(false);
		setSubmitting(false);
		setResourceBadges({});
	}, [isOpen, eventId]);

	useEffect(() => {
		if (!isOpen || !eventId) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setEvent(null);

		// Use the filter-selected timezone (already in scope as `timezone`
		// — derived from `state.calendarMonth.filters.timezone` with the
		// bootstrap fallback). Backend uses this to localize the event's
		// time bucket for the response — must match the user's filter
		// rather than browser-local. Re-fetch when filter tz changes.
		CalendarApi.getEvent(eventId, timezone || 'UTC', eventOrgUnique || undefined)
			.then(res => {
				if (cancelled) return;
				const data = unwrap(res);
				setEvent(normalizeEventDetail(data));
			})
			.catch(err => {
				if (cancelled) return;
				setError(err?.response?.data?.error?.[0] || err?.message || 'Failed to load event');
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isOpen, eventId, eventOrgUnique, timezone, refetchKey]);

	// Lazily fetch discrepancy count (badge) + airworthy status per aircraft resource.
	// Airworthy is based on inspections.danger (legacy parity), not discrepancy count.
	// Non-blocking — badges appear after resource links render.
	useEffect(() => {
		if (!event || !Array.isArray(event.eventResources)) return;
		if (!perms?.canViewResource) return;
		const eventOrgUq = event.organizationUnique || eventOrgUnique || orgUnique;
		const eventTz = timezone || 'UTC';
		event.eventResources.forEach(er => {
			const aircraft = er.aircraft || er.resource?.aircraft;
			if (!aircraft?.id) return;
			const aircraftId = aircraft.id;

			// Discrepancy count badge — only if user can see discrepancies
			if (perms.canViewDiscrepancies) {
				DiscrepanciesApi.getAircraftDiscrepancies(aircraftId, eventOrgUq)
					.then(res => {
						const list = Array.isArray(unwrap(res)) ? unwrap(res) : [];
						setResourceBadges(prev => ({
							...prev,
							[aircraftId]: { ...(prev?.[aircraftId] || {}), count: list.length }
						}));
					})
					.catch(() => {});
			}

			// Airworthy icon — 3-state ('unknown' | 'airworthy' | 'unairworthy').
			// Uses the shared util so Calendar Month V2 stays in lock-step with
			// View Aircraft / Hangars. The util normalizes boolean / number /
			// string truthy forms for `danger` and `affectsAirworthiness`, so
			// Symfony's boolean payloads no longer get silently dropped (was the
			// root cause of the n11qf2 parity bug — Rem16 Bug S).
			if (perms.canViewInspections) {
				ResourcesApi.getInspections(aircraftId, eventTz, eventOrgUq)
					.then(res => {
						const list = Array.isArray(unwrap(res)) ? unwrap(res) : [];
						const airworthyStatus = getAirworthinessStatusFromInspections(list);
						setResourceBadges(prev => ({
							...prev,
							[aircraftId]: { ...(prev?.[aircraftId] || {}), airworthyStatus }
						}));
					})
					.catch(() => {});
			}
		});
	}, [event?.id, perms?.canViewResource, perms?.canViewDiscrepancies, perms?.canViewInspections]); // eslint-disable-line react-hooks/exhaustive-deps

	// ─── Save (Edit screen) ─────────────────────────────────────────────────
	// Called either directly (non-recurring or scope dialog already chose) or
	// via the recurring scope dialog flow. `payload` already comes from
	// `buildEventPayload` inside EventFormBody — we may need to override
	// `eventsForAction` based on the user's scope pick.
	const performSave = useCallback(
		async (payload, formOrgUnique, scopeOverride) => {
			const isMaintenance = (event?.type || '').toLowerCase().includes('maint');
			if (isMaintenance) {
				const resourceId = getMaintenanceResourceId(event);
				if (resourceId == null) {
					addToast({
						title: 'Save failed',
						description: 'Cannot resolve maintenance resource',
						color: 'danger'
					});
					return { ok: false };
				}
				const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
				setSubmitting(true);
				try {
					const res = await ResourcesApi.updateMaintenance(
						resourceId,
						eventId,
						{
							startTime: payload.startTime,
							finishTime: payload.finishTime,
							comments: payload.comments,
							// Resource location tz (NOT filter/browser tz). See Rem17 — Symfony
							// `MaintenanceManager.php` reinterprets the wall-clock in the
							// resource location, so filter-tz formatting shifts the time.
							timezoneId: payload.locationTimezoneId || payload.timezone
						},
						targetOrg
					);
					const body = res?.data ?? res;
					if (body?.success === false) {
						const msg = (body.error && body.error[0]) || 'Save failed';
						addToast({ title: 'Save failed', description: msg, color: 'danger' });
						setSubmitting(false);
						return { ok: false };
					}
					addToast({ title: 'Event updated', color: 'success' });
					setSubmitting(false);
					onClose?.();
					onUpdated?.();
					return { ok: true };
				} catch (err) {
					const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
					addToast({ title: 'Save failed', description: msg, color: 'danger' });
					setSubmitting(false);
					return { ok: false };
				}
			}

			const targetOrg = formOrgUnique || event?.organizationUnique || orgUnique;
			// Final guard: backend requires `recurringAction='save'` +
			// `eventsForAction` on EVERY save against a recurring series, even when
			// the user toggled the form's `recurring` flag off (single-instance
			// edit) — `buildEventPayload` only sets `eventsForAction` when the
			// form is still flagged recurring (`willRecur`). Without this guard
			// the backend currently masks the gap with a defensive `'current'`
			// fallback and a Monolog warning. The parent already owns the
			// authoritative `event.recurring` flag, so we patch the payload here.
			// Precedence: explicit scope from dialog/inline picker > whatever
			// `buildEventPayload` set (recurring-form path) > `'current'` default.
			const isEventRecurring =
				event?.recurring === true || event?.recurring === '1' || event?.recurring === 1;
			const finalPayload = isEventRecurring
				? {
						...payload,
						recurringAction: 'save',
						eventsForAction: scopeOverride || payload.eventsForAction || 'current'
					}
				: payload;
			const isOwn = isSubmittedMyEvent(finalPayload, currentUserId, resourcesMap);
			setSubmitting(true);
			try {
				const res = isOwn
					? await CalendarApi.updateMyEvent(eventId, finalPayload, targetOrg)
					: await CalendarApi.updateEvent(eventId, finalPayload, targetOrg);
				const body = res?.data ?? res;
				if (body?.success === false) {
					const msg = (body.error && body.error[0]) || 'Save failed';
					addToast({ title: 'Save failed', description: msg, color: 'danger' });
					setSubmitting(false);
					return { ok: false };
				}
				addToast({ title: 'Event updated', color: 'success' });
				setSubmitting(false);
				onClose?.();
				onUpdated?.();
				return { ok: true };
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
				addToast({ title: 'Save failed', description: msg, color: 'danger' });
				setSubmitting(false);
				return { ok: false };
			}
		},
		[
			event?.type,
			event?.eventResources,
			event?.organizationUnique,
			event?.recurring,
			eventOrgUnique,
			orgUnique,
			currentUserId,
			resourcesMap,
			eventId,
			onClose,
			onUpdated
		]
	);

	const handleSave = useCallback(
		async (payload, formOrgUnique, inlineScope) => {
			// Recurring + canManageRecurrent → use the inline scope picked in the
			// form's recurring section (Current / Following / All); fall back to
			// the separate scope dialog if the form didn't supply one (e.g. user
			// turned off Recurring before submitting and the inline picker hid).
			// Recurring + NO canManageRecurrent → buildEventPayload already
			// stripped recurring fields; we still need to send eventsForAction='current'
			// because the event IS recurring server-side and the save validation
			// gate rejects null/omitted (`null !== CURRENT` permission trap).
			const isEventRecurring =
				event?.recurring === true || event?.recurring === '1' || event?.recurring === 1;
			const canManageRecurrent = !!perms?.canManageRecurrent;
			if (isEventRecurring && canManageRecurrent) {
				if (inlineScope) {
					return performSave(payload, formOrgUnique, inlineScope);
				}
				setScopeDialog({ kind: 'edit', payload, formOrgUnique });
				return { ok: false }; // keep form open until dialog confirms
			}
			if (isEventRecurring && !canManageRecurrent) {
				return performSave(payload, formOrgUnique, 'current');
			}
			return performSave(payload, formOrgUnique);
		},
		[event?.recurring, perms?.canManageRecurrent, performSave]
	);

	// ─── Delete ─────────────────────────────────────────────────────────────
	const performDelete = useCallback(
		async scope => {
			const isMaintenance = (event?.type || '').toLowerCase().includes('maint');
			if (isMaintenance) {
				const resourceId = getMaintenanceResourceId(event);
				if (resourceId == null) {
					addToast({
						title: 'Delete failed',
						description: 'Cannot resolve maintenance resource',
						color: 'danger'
					});
					return;
				}
				const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
				setSubmitting(true);
				try {
					const res = await ResourcesApi.deleteMaintenance(resourceId, eventId, targetOrg);
					const body = res?.data ?? res;
					if (body?.success === false) {
						const msg = (body.error && body.error[0]) || 'Delete failed';
						addToast({ title: 'Delete failed', description: msg, color: 'danger' });
						setSubmitting(false);
						return;
					}
					addToast({ title: 'Event deleted', color: 'success' });
					setSubmitting(false);
					onClose?.();
					onDeleted?.();
				} catch (err) {
					const msg = err?.response?.data?.error?.[0] || err?.message || 'Delete failed';
					addToast({ title: 'Delete failed', description: msg, color: 'danger' });
					setSubmitting(false);
				}
				return;
			}

			const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
			const params = scope ? { eventsForAction: scope } : {};
			setSubmitting(true);
			try {
				const res = await CalendarApi.deleteEvent(eventId, params, targetOrg);
				const body = res?.data ?? res;
				if (body?.success === false) {
					const msg = (body.error && body.error[0]) || 'Delete failed';
					addToast({ title: 'Delete failed', description: msg, color: 'danger' });
					setSubmitting(false);
					return;
				}
				addToast({ title: 'Event deleted', color: 'success' });
				setSubmitting(false);
				onClose?.();
				onDeleted?.();
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Delete failed';
				addToast({ title: 'Delete failed', description: msg, color: 'danger' });
				setSubmitting(false);
			}
		},
		[
			event?.type,
			event?.eventResources,
			event?.organizationUnique,
			eventOrgUnique,
			orgUnique,
			eventId,
			onClose,
			onDeleted
		]
	);

	const handleDeleteClick = useCallback(() => {
		const isEventRecurring =
			event?.recurring === true || event?.recurring === '1' || event?.recurring === 1;
		const canManageRecurrent = !!perms?.canManageRecurrent;
		if (isEventRecurring && canManageRecurrent) {
			setScopeDialog({ kind: 'delete' });
			return;
		}
		// Non-recurring OR recurring-without-perm: simple confirm. The
		// without-perm trap requires omitting `eventsForAction` so backend
		// defaults the route correctly.
		setDeleteConfirm(true);
	}, [event?.recurring, perms?.canManageRecurrent]);

	const handleEditClick = useCallback(() => setScreen('edit'), []);
	const handleEditCancel = useCallback(() => setScreen('view'), []);

	// In-modal Check Out flow (Phase 2.1 Sub 2). Replaces the prior
	// `goTo('event-checkout')` redirect at the Status block. Sub 5 wires
	// `onSubmitted` to dispatch month-events refresh + close. Past events
	// follow the same Check Out → Check In sequence as regular events
	// (Sub 10 revert — the combined past-flow screen was feature creep
	// vs legacy parity).
	const handleCheckOutClick = useCallback(() => setScreen('checkout'), []);
	const handleCheckOutBack = useCallback(() => setScreen('view'), []);

	// In-modal Check In flow (Phase 2.1 Sub 6). Replaces `goTo('event-checkin')`.
	const handleCheckInClick = useCallback(() => setScreen('checkin'), []);
	const handleCheckInBack = useCallback(() => setScreen('view'), []);

	const effectiveOrgUnique = event?.organizationUnique || eventOrgUnique || orgUnique;

	const aircraftId = useMemo(() => {
		if (!Array.isArray(event?.eventResources)) return null;
		for (const er of event.eventResources) {
			const a = pickAircraft(er);
			if (a?.id) return a.id;
		}
		return null;
	}, [event?.eventResources]);

	const handleWbOpen = useCallback(() => setScreen('weight-balance'), []);

	const handleWbBack = useCallback(() => setScreen('view'), []);

	// Cancel Check Out / Cancel Check In (Phase 2.1 Sub 14). Each opens a
	// confirmation modal first; on confirm, POSTs to the Symfony cancel
	// endpoint and refreshes the month grid via parent's `onUpdated`.
	const handleCancelCheckOutClick = useCallback(() => setCancelCheckOutConfirm(true), []);
	const handleCancelCheckInClick = useCallback(() => setCancelCheckInConfirm(true), []);

	const performCancelCheckOut = useCallback(async () => {
		if (!eventId) return;
		setSubmitting(true);
		try {
			// Rem18 Task 1: cross-org override — pass event's org explicitly.
			const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
			const res = await CalendarApi.cancelCheckOut(eventId, timezone || 'UTC', targetOrg);
			const body = res?.data ?? res;
			if (body?.success === false) {
				const msg = (body.error && body.error[0]) || 'Cancel Check Out failed';
				addToast({ title: 'Cancel Check Out failed', description: msg, color: 'danger' });
				return;
			}
			addToast({ title: 'Check Out cancelled', color: 'success' });
			// Rem21 Sub 10 — stay in modal, re-fetch event detail so the
			// returning view shows the reverted state.
			onUpdated?.();
			setRefetchKey(k => k + 1);
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Cancel Check Out failed';
			addToast({ title: 'Cancel Check Out failed', description: msg, color: 'danger' });
		} finally {
			setSubmitting(false);
			setCancelCheckOutConfirm(false);
		}
	}, [eventId, timezone, event?.organizationUnique, eventOrgUnique, orgUnique, onUpdated]);

	const performCancelCheckIn = useCallback(async () => {
		if (!eventId) return;
		setSubmitting(true);
		try {
			// Rem18 Task 1: cross-org override.
			const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
			const res = await CalendarApi.cancelCheckIn(eventId, timezone || 'UTC', targetOrg);
			const body = res?.data ?? res;
			if (body?.success === false) {
				const msg = (body.error && body.error[0]) || 'Cancel Check In failed';
				addToast({ title: 'Cancel Check In failed', description: msg, color: 'danger' });
				return;
			}
			addToast({ title: 'Check In cancelled', color: 'success' });
			// Rem21 Sub 10 — stay in modal, re-fetch event detail.
			onUpdated?.();
			setRefetchKey(k => k + 1);
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Cancel Check In failed';
			addToast({ title: 'Cancel Check In failed', description: msg, color: 'danger' });
		} finally {
			setSubmitting(false);
			setCancelCheckInConfirm(false);
		}
	}, [eventId, timezone, event?.organizationUnique, eventOrgUnique, orgUnique, onUpdated]);

	// ─── Cancel (reason + optional scope) ─────────────────────────────────
	const handleCancelClick = useCallback(() => setCancelDialog(true), []);

	const performCancel = useCallback(
		async ({ reason, scope }) => {
			const targetOrg = event?.organizationUnique || eventOrgUnique || orgUnique;
			const isOwn = isSubmittedMyEvent(event, currentUserId, resourcesMap);
			const params = { timezone };
			if (scope) params.eventsForAction = scope;
			try {
				const res = isOwn
					? await CalendarApi.cancelMyEvent(eventId, { reason }, params, targetOrg)
					: await CalendarApi.cancelEvent(eventId, { reason }, params, targetOrg);
				const body = res?.data ?? res;
				if (body?.success === false) {
					const msg = (body.error && body.error[0]) || 'Cancel failed';
					addToast({ title: 'Cancel failed', description: msg, color: 'danger' });
					return;
				}
				addToast({ title: 'Event cancelled', color: 'success' });
				setCancelDialog(false);
				onClose?.();
				onUpdated?.();
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Cancel failed';
				addToast({ title: 'Cancel failed', description: msg, color: 'danger' });
			}
		},
		[
			event,
			eventOrgUnique,
			orgUnique,
			currentUserId,
			resourcesMap,
			eventId,
			timezone,
			onClose,
			onUpdated
		]
	);

	// ─── Add to Waiting List (clone into create) ──────────────────────────
	const handleWaitingListClick = useCallback(() => setWaitingModal(true), []);
	const handleWaitingClose = useCallback(() => setWaitingModal(false), []);
	const handleWaitingCreated = useCallback(() => {
		setWaitingModal(false);
		onClose?.();
		onUpdated?.();
	}, [onClose, onUpdated]);

	const handleResourceClick = useCallback(
		resource => {
			setPreviousScreen(screen);
			setSelectedResource(resource);
			setScreen('resource');
		},
		[screen]
	);

	const handleResourceBack = useCallback(() => {
		setScreen(previousScreen || 'view');
		setSelectedResource(null);
	}, [previousScreen]);

	const handleEditResourceClick = useCallback(
		resourceId => {
			if (!perms?.canViewResource) return;
			// `resourceId` from the chip is the scheduled-resource row ID (resourceID).
			// We need the entity (aircraft/simulator) ID to call the correct endpoint.
			const er = (event?.eventResources || []).find(
				r => String(r.resourceID ?? r.resourceId ?? r.resource?.id ?? '') === String(resourceId)
			);
			if (!er) return;
			// Mirror view-mode resolver — support nested, sub-nested, and flat
			// (`er.resource.type === 'simulator'`) Symfony shapes.
			const aircraft = pickAircraft(er);
			const simulator = pickSimulator(er);
			const type = aircraft ? 'aircraft' : simulator ? 'simulator' : null;
			if (!type) return;
			const entity = type === 'aircraft' ? aircraft : simulator;
			const entityId = entity?.id ?? er.resource?.id ?? null;
			if (entityId == null) return;
			const label =
				type === 'aircraft'
					? entity.registrationNumber || entity.regNumb || entity.name || 'Aircraft'
					: entity.name || 'Simulator';
			handleResourceClick({ id: entityId, type, label });
		},
		[event, perms?.canViewResource, handleResourceClick]
	);

	const handleDiscrepancyClick = useCallback(discr => {
		setSelectedDiscrepancy(discr);
		setScreen('discrepancy');
	}, []);

	const handleDiscrepancyBack = useCallback(() => {
		setScreen('resource');
		setSelectedDiscrepancy(null);
	}, []);

	const handleGoToView = useCallback(() => {
		setScreen('view');
		setSelectedResource(null);
		setSelectedDiscrepancy(null);
	}, []);

	// Customer fields filtered to event's org for the edit form hydration.
	// Memoized so EventFormBody's hydration effect does not re-fire on every
	// parent re-render (e.g. submit re-render flipping `submitting`), which
	// would reset local form state from the stale `event` payload.
	const orgCustomFields = useMemo(() => {
		const map = customFieldsMap || {};
		const list = Array.isArray(map) ? map : Object.values(map);
		const targetOrg = event?.organizationUnique;
		return list
			.filter(cf => cf && !cf.trash)
			.filter(cf => !targetOrg || !cf.organizationUnique || cf.organizationUnique === targetOrg)
			.map(cf => ({
				id: String(cf.id),
				name: cf.name || `Field ${cf.id}`,
				type: String(cf.type || 'text').toLowerCase(),
				required: cf.required === true || cf.required === 1 || cf.required === '1'
			}));
	}, [customFieldsMap, event?.organizationUnique]);

	const initialFormState = useMemo(
		() =>
			event
				? mapEventToFormState(
						normalizeDetailEvent(event),
						orgCustomFields,
						event.locationTimezoneId || timezone || 'UTC'
					)
				: null,
		[event, orgCustomFields, timezone]
	);

	const isMaintenance = (event?.type || '').toLowerCase().includes('maint');
	const { phone: isNarrow, navCompact } = useBreakpoint();
	const bigScreen =
		screen === 'resource' || screen === 'discrepancy' || screen === 'weight-balance';

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size={isNarrow ? 'md' : bigScreen ? '4xl' : '3xl'}
				placement={isNarrow ? 'bottom' : 'center'}
				scrollBehavior="inside"
				classNames={{
					base: isNarrow
						? 'shadow-cal-elev-3 max-h-[90vh] m-0 rounded-b-none rounded-t-lg'
						: screen === 'weight-balance'
							? // Tablet (640–1023): viewport-bounded so the W1 2-column W&B
								// layout fits with NO modal-level horizontal scroll. Desktop
								// (≥1024): near-full viewport — the owner wants EVERY W&B row
								// visible at once, so the modal claims the height the default
								// cap (~100vh-7.5rem) was wasting and widens past 1200px when
								// the screen allows (the fields table + fuel cases + envelope
								// then all fit with no inner scrolling).
								navCompact
								? 'shadow-cal-elev-3 !max-w-[calc(100vw-2rem)] max-h-[90vh]'
								: 'shadow-cal-elev-3 !max-w-[min(1560px,calc(100vw-2rem))] !max-h-[calc(100vh-1.5rem)]'
							: 'shadow-cal-elev-3'
				}}
				motionProps={
					isNarrow
						? {
								variants: {
									enter: { y: 0, opacity: 1, transition: { duration: 0.2, ease: 'easeOut' } },
									exit: { y: 40, opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } }
								}
							}
						: undefined
				}
			>
				<ModalContent>
					{loading && (
						<ModalBody className="flex items-center justify-center py-16">
							<Spinner size="lg" />
						</ModalBody>
					)}
					{error && (
						<>
							<ModalHeader>Error</ModalHeader>
							<ModalBody>
								<p className="text-danger">{error}</p>
							</ModalBody>
							<ModalFooter>
								<Button variant="light" onPress={onClose}>
									Close
								</Button>
							</ModalFooter>
						</>
					)}
					{!loading && !error && event && screen === 'view' && (
						<EventContent
							event={event}
							onClose={onClose}
							orgUnique={orgUnique}
							eventId={eventId}
							perms={perms}
							flags={flags}
							onEditClick={handleEditClick}
							onDeleteClick={handleDeleteClick}
							onCancelClick={handleCancelClick}
							onWaitingListClick={handleWaitingListClick}
							onResourceClick={handleResourceClick}
							onCheckOutClick={handleCheckOutClick}
							onCheckInClick={handleCheckInClick}
							onCancelCheckOutClick={handleCancelCheckOutClick}
							onCancelCheckInClick={handleCancelCheckInClick}
							resourceBadges={resourceBadges}
							onWbClick={handleWbOpen}
							orgCustomFields={orgCustomFields}
						/>
					)}
					{!loading && !error && event && screen === 'resource' && selectedResource && (
						<ResourcePanel
							key={resourcePanelKey}
							resource={selectedResource}
							eventId={eventId}
							organizationId={event?.organizationId ?? event?.organization?.id ?? null}
							orgUnique={event?.organizationUnique || eventOrgUnique || orgUnique}
							eventTimezone={event?.locationTimezoneId || timezone || 'UTC'}
							hasCheckOut={event?.checkOutTime != null}
							hasCheckIn={Number(event?.checkInTime) > 0}
							onBack={handleResourceBack}
							perms={perms}
							currentUserId={currentUserId}
							pilotId={event?.pilotId}
							userSettings={userSettings}
							eventTypeLabel={resolveEventType(event).label}
							onDiscrepancyClick={handleDiscrepancyClick}
						/>
					)}
					{!loading && !error && event && screen === 'discrepancy' && selectedDiscrepancy && (
						<DiscrepancyView
							discrepancy={selectedDiscrepancy}
							orgUnique={event?.organizationUnique || eventOrgUnique || orgUnique}
							currentUserId={currentUserId}
							perms={perms}
							onBack={handleDiscrepancyBack}
							onBackToEvent={handleGoToView}
							onDelete={() => setResourcePanelKey(k => k + 1)}
							userSettings={userSettings}
							eventTypeLabel={resolveEventType(event).label}
							resourceLabel={selectedResource?.label}
						/>
					)}
					{!loading && !error && event && screen === 'checkout' && (
						<CheckOutScreen
							event={event}
							eventId={eventId}
							orgUnique={event?.organizationUnique || eventOrgUnique || orgUnique}
							timezone={event?.locationTimezoneId || timezone || 'UTC'}
							eventTypeLabel={resolveEventType(event).label}
							onBack={handleCheckOutBack}
							onClose={onClose}
							onSubmitted={() => {
								// Rem21 Sub 10 — Check Out submit returns to event
								// view inside the same modal; the refetchKey bump
								// re-fires the event-detail GET so the returning
								// view shows the new status (Check In + Cancel
								// Check Out buttons swap in). Parent's `onUpdated`
								// still refreshes the calendar grid.
								onUpdated?.();
								setScreen('view');
								setRefetchKey(k => k + 1);
							}}
						/>
					)}
					{!loading && !error && event && screen === 'checkin' && (
						<CheckInScreen
							event={event}
							eventId={eventId}
							orgUnique={event?.organizationUnique || eventOrgUnique || orgUnique}
							timezone={event?.locationTimezoneId || timezone || 'UTC'}
							eventTypeLabel={resolveEventType(event).label}
							onBack={handleCheckInBack}
							onClose={onClose}
							onSubmitted={() => {
								// Rem21 Sub 10 — Check In submit returns to event
								// view (status → Completed, Cancel Check In
								// surfaces). See Check Out branch for rationale.
								onUpdated?.();
								setScreen('view');
								setRefetchKey(k => k + 1);
							}}
						/>
					)}
					{!loading && !error && event && screen === 'edit' && (
						<>
							<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
								<span style={{ color: 'hsl(var(--heroui-primary))' }}>
									{isMaintenance ? <Wrench className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
								</span>
								<span
									className="text-lg font-bold flex-1"
									style={{ color: 'hsl(var(--heroui-foreground))' }}
								>
									{isMaintenance ? 'Edit Maintenance Event' : 'Edit Event'}
								</span>
							</ModalHeader>
							<EventFormBody
								mode="edit"
								isOpen={isOpen && screen === 'edit'}
								orgUnique={orgUnique}
								eventId={eventId}
								eventOrgUnique={event?.organizationUnique || eventOrgUnique}
								initialFormState={initialFormState}
								onSave={handleSave}
								onCancel={handleEditCancel}
								// Cross-org parity — the recurrence gate inside
								// the form must follow the EVENT's org, not the
								// page org. Tri-state: `null` while perms still
								// loading (cross-org fan-out), boolean once
								// resolved. EventFormBody stays optimistic during
								// the null window so async perm load can't wipe
								// freshly-hydrated `recurring=true` state.
								effectiveCanManageRecurrent={perms == null ? null : !!perms.canManageRecurrent}
								onResourceClick={perms?.canViewResource ? handleEditResourceClick : undefined}
							/>
						</>
					)}
					{!loading && !error && event && screen === 'weight-balance' && (
						<WeightBalanceScreen
							event={event}
							eventId={eventId}
							orgUnique={effectiveOrgUnique}
							aircraftId={aircraftId}
							onBack={handleWbBack}
							onSaved={() => {
								setScreen('view');
								setRefetchKey(k => k + 1);
								onUpdated?.();
							}}
							/* Rem38 M6 — passenger CRUD auto-save: refresh the month
							   grid only. Must NOT setScreen('view') or bump refetchKey
							   (that unmounts the W&B screen + triggers a slow event
							   refetch on every passenger add/remove/move). */
							onPersisted={() => {
								onUpdated?.();
							}}
						/>
					)}
				</ModalContent>
			</Modal>

			{/* Recurring scope dialog (Edit OR Delete). Sequential, not stacked. */}
			<RecurringScopeConfirm
				isOpen={!!scopeDialog}
				onClose={() => setScopeDialog(null)}
				onConfirm={async scope => {
					const dialog = scopeDialog;
					setScopeDialog(null);
					if (!dialog) return;
					if (dialog.kind === 'edit') {
						await performSave(dialog.payload, dialog.formOrgUnique, scope);
					} else if (dialog.kind === 'delete') {
						await performDelete(scope);
					}
				}}
				actionLabel={scopeDialog?.kind === 'delete' ? 'Delete' : 'Save'}
				options={scopeDialog?.kind === 'delete' ? DELETE_RECURRING_OPTIONS : EDIT_RECURRING_OPTIONS}
				eventTitle={event?.comments || event?.description || ''}
			/>

			{/* Simple Delete confirm — non-recurring path or recurring-without-perm. */}
			<Modal
				isOpen={deleteConfirm}
				onClose={() => setDeleteConfirm(false)}
				size="sm"
				placement="center"
			>
				<ModalContent>
					<ModalHeader>Delete this event?</ModalHeader>
					<ModalBody>
						<p className="text-sm text-default-600">This event will be permanently removed.</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={() => setDeleteConfirm(false)}>
							Cancel
						</Button>
						<Button
							color="danger"
							isLoading={submitting}
							onPress={async () => {
								setDeleteConfirm(false);
								await performDelete(null);
							}}
						>
							Delete
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Cancel Check Out confirm — Phase 2.1 Sub 14. Reverts the
			    in-flight state so the user can re-do the checkout. */}
			<Modal
				isOpen={cancelCheckOutConfirm}
				onClose={() => setCancelCheckOutConfirm(false)}
				size="sm"
				placement="center"
			>
				<ModalContent>
					<ModalHeader>Cancel Check Out?</ModalHeader>
					<ModalBody>
						<p className="text-sm text-default-600">
							The event will return to its pre-flight state. Any unsaved in-flight data will be
							discarded.
						</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={() => setCancelCheckOutConfirm(false)}>
							Keep
						</Button>
						<Button color="warning" isLoading={submitting} onPress={performCancelCheckOut}>
							Cancel Check Out
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Cancel Check In confirm — Phase 2.1 Sub 14. Heavier copy
			    because this undoes the entire flight (saved counters/comments). */}
			<Modal
				isOpen={cancelCheckInConfirm}
				onClose={() => setCancelCheckInConfirm(false)}
				size="sm"
				placement="center"
			>
				<ModalContent>
					<ModalHeader>Cancel Check In?</ModalHeader>
					<ModalBody>
						<p className="text-sm text-default-600">
							This will undo the completed flight. The event will return to the in-flight state and
							any saved meter values, comments, or custom fields from check-in will be discarded.
						</p>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={() => setCancelCheckInConfirm(false)}>
							Keep
						</Button>
						<Button color="warning" isLoading={submitting} onPress={performCancelCheckIn}>
							Cancel Check In
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Cancel-with-reason dialog. Scope picker shown only for recurring
			    events with `canManageRecurrent` (mirrors delete-scope rule). */}
			<CancelEventConfirm
				isOpen={cancelDialog}
				onClose={() => setCancelDialog(false)}
				onConfirm={performCancel}
				showScope={
					!!perms?.canManageRecurrent &&
					(event?.recurring === true || event?.recurring === '1' || event?.recurring === 1)
				}
				eventTitle={event?.comments || event?.description || ''}
			/>

			{/* Add to Waiting List — opens a fresh Create modal pre-filled with
			    this event's data. `forceAddToWaiting=true` flips the payload's
			    `addToWaiting` flag via `buildEventPayload` opts. */}
			<CreateEventModal
				isOpen={waitingModal}
				onClose={handleWaitingClose}
				orgUnique={event?.organizationUnique || eventOrgUnique || orgUnique}
				initialDayTs={event?.startTime ? Number(event.startTime) : null}
				initialFormState={initialFormState}
				forceAddToWaiting
				headerLabel="Add to Waiting List"
				onCreated={handleWaitingCreated}
			/>
		</>
	);
}

function EventContent({
	event,
	onClose,
	orgUnique,
	eventId,
	perms,
	flags,
	onEditClick,
	onDeleteClick,
	onCancelClick,
	onWaitingListClick,
	onResourceClick,
	onCheckOutClick,
	onCheckInClick,
	onCancelCheckOutClick,
	onCancelCheckInClick,
	resourceBadges,
	onWbClick,
	// The org's custom-field definitions (already filtered to the event's
	// org by the parent). Rendered read-only under Instructor in EVERY state —
	// legacy parity; the V2 modal had only used them to hydrate the edit form.
	orgCustomFields = []
}) {
	// `flags` (Phase 2.2 Sub 3) is the per-button gate map from
	// `computeEventPermissions`. Treat undefined as the "perms still loading"
	// case: short-circuit every button below by falling through to false.
	const f = flags || {};
	const weightUnit = useSelector(selectWeightUnit);
	// Two-tier time display, legacy parity:
	//   - `eventTz` drives the TOP line (date + time in the event's location
	//     timezone). Falls back to filterTz when the event has no location.
	//   - `filterTz` drives the BOTTOM line (date + time + real UTC offset
	//     in the timezone currently selected in CalendarFilterBar).
	const filterTz = useSelector(
		state => pickCalendarFilters(state)?.timezone || pickCalendarBootstrap(state)?.timezone || 'UTC'
	);
	const eventTz = event.locationTimezoneId || filterTz;
	const currentUserId = useSelector(
		state => state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);
	const activityTypesMap = useSelector(
		state => pickCalendarRefs(state)?.activityTypes || EMPTY_ACTIVITY_TYPES
	);
	const { label: typeLabel, color: typeColor, isMaintenance } = resolveEventType(event);
	const status = resolveStatus(event);

	// ── Instructor time on a completed event (legacy parity) ─────────────────
	// Legacy showed `Instructor Name (Total: 3.00)` once checked in, from
	// `resourcesDiff[instructorID]`. The Symfony detail DTO carries no such
	// map, so `pickInstructorTotal` tries the DTO first and falls back to the
	// per-event counter history, fetched only when it is actually needed:
	// completed event + an instructor + nothing in the DTO already.
	const isCompleted =
		Number(event.checkInTime) > 0 ||
		event.completed === true ||
		event.completed === 1 ||
		event.completed === '1';
	const hasInstructor = event.instructorId != null || event.instructorID != null;
	const organizationIdForHistory = event.organizationId ?? event.organization?.id ?? null;
	const [instructorHistory, setInstructorHistory] = useState(null);
	const totalFromDto = useMemo(() => pickInstructorTotal(event), [event]);
	useEffect(() => {
		let cancelled = false;
		setInstructorHistory(null);
		if (!isCompleted || !hasInstructor || totalFromDto != null) return undefined;
		if (event.id == null || organizationIdForHistory == null) return undefined;
		CounterApi.getEventHistory(event.id, organizationIdForHistory)
			.then(res => {
				if (!cancelled) setInstructorHistory(unwrap(res) ?? res);
			})
			.catch(() => {
				/* decoration only — the name still renders without a total */
			});
		return () => {
			cancelled = true;
		};
	}, [event.id, isCompleted, hasInstructor, totalFromDto, organizationIdForHistory]);
	const instructorTotal =
		isCompleted && hasInstructor
			? (totalFromDto ?? pickInstructorTotal(event, instructorHistory))
			: null;

	// ── Custom fields, read-only, every state ────────────────────────────────
	const customFieldRows = useMemo(
		() => (isMaintenance ? [] : buildCustomFieldRows(event, orgCustomFields)),
		[event, orgCustomFields, isMaintenance]
	);

	const activityTypeId = event.activityTypeId ?? event.activityType ?? null;
	const activityTypeEntry = activityTypeId ? activityTypesMap[String(activityTypeId)] : null;
	const regNum = event.registrationNumber || event.regNumb;
	const pilotFull = [event.pilotName, event.pilotSurname].filter(Boolean).join(' ');
	const coPilotFull = [event.coPilotName, event.coPilotSurname].filter(Boolean).join(' ');
	const instructorFull = [event.instructorName, event.instructorSurname].filter(Boolean).join(' ');
	const isMe = id => currentUserId != null && id != null && String(id) === String(currentUserId);
	const pilotIsMe = isMe(event.pilotId);
	const coPilotIsMe = isMe(event.coPilotId);
	const instructorIsMe = isMe(event.instructorId);

	// When the person is NOT the current user, render their name as a link to
	// the legacy user review page (opens in a new tab). `target=_blank` +
	// `rel=noopener noreferrer` is required so the external tab can't access
	// `window.opener`. Inline style overrides Bootstrap's global anchor color.
	const renderPerson = (fullName, userId, showYou) => {
		if (!fullName) return <span className="text-default-400">None</span>;
		const reviewUrl =
			userId && effectiveOrgUnique ? `/org/${effectiveOrgUnique}/users/review/${userId}/` : null;
		return (
			<div className="flex items-center gap-2 flex-wrap">
				{reviewUrl && !showYou ? (
					<a
						href={reviewUrl}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: 'hsl(var(--heroui-primary))', textDecoration: 'underline' }}
					>
						{fullName}
					</a>
				) : (
					<span>{fullName}</span>
				)}
				{showYou && (
					<Chip size="sm" variant="flat" color="primary">
						It&apos;s you
					</Chip>
				)}
			</div>
		);
	};

	// Event may live in a different org than the current URL-path org when
	// multi-org is on. Prefer the event's own `organizationUnique` so the
	// legacy Day View opens in the right org context; fall back to the page
	// orgUnique if the event payload doesn't carry it.
	const effectiveOrgUnique = event?.organizationUnique || orgUnique;
	const buildViewUrl = view => {
		if (!effectiveOrgUnique || !eventId) return null;
		return `/org/${effectiveOrgUnique}/calendar/${view}/#?action=event-view&event=${eventId}&org=${effectiveOrgUnique}`;
	};
	const dayViewUrl = buildViewUrl('day');
	const viewLinks = [
		{ key: 'month', label: 'Month View', href: buildViewUrl('month') },
		{ key: 'week', label: 'Week View', href: buildViewUrl('week') },
		{ key: 'day', label: 'Day View', href: dayViewUrl }
	];

	// All action buttons navigate to the legacy Day View page which still
	// owns the full CRUD / check-in / check-out flows. When Phase 3 ports
	// those flows to React, replace with in-modal handlers.
	const goTo = action => () => {
		if (!dayViewUrl || typeof window === 'undefined') return;
		const base = dayViewUrl.split('#')[0];
		window.location.href = `${base}#?action=${action}&event=${eventId}`;
	};
	const isTruthy = v => v === '1' || v === true || v === 1;
	const isRecurring = isTruthy(event.recurring);

	// SYM-155: W&B summary. The single-event DETAIL response has no list-DTO
	// `passengersAmount` / `seatsAmount`; it ships `freeAndOccupiedSeats`
	// ({ freeSeats, occupiedSeats, totalSeats }). Fall back to it so the
	// passenger / seat counts are correct in the detail modal.
	const fos = event.freeAndOccupiedSeats || null;
	let passengersAmount = Number(event.passengersAmount);
	if (!Number.isFinite(passengersAmount) || passengersAmount <= 0) {
		passengersAmount = fos ? Number(fos.occupiedSeats) || 0 : 0;
	}
	let seatsAmount = Number(event.seatsAmount);
	if ((!Number.isFinite(seatsAmount) || seatsAmount <= 0) && fos) {
		seatsAmount = Number(fos.totalSeats) || 0;
	}
	const hasWb = passengersAmount > 0 && event.availableWeight != null;
	// Manage-W&B link gate. The backend ships `additionalData.weightAndBalanceEnable`
	// (the authoritative legacy-parity flag) — use it when present. Fall back to
	// the aircraft-resource heuristic only if the flag is absent (older payloads).
	const wbEnableFlag = event.additionalData?.weightAndBalanceEnable;
	const canManageWb =
		wbEnableFlag != null
			? !!wbEnableFlag
			: Array.isArray(event.eventResources) && event.eventResources.some(er => pickAircraft(er));
	const wbAvailNum = hasWb ? parseFloat(event.availableWeight) : NaN;
	const wbOver = Number.isFinite(wbAvailNum) && wbAvailNum < 0;
	const seatsText =
		Number.isFinite(seatsAmount) && seatsAmount > 0
			? `${passengersAmount} / ${seatsAmount}`
			: `${passengersAmount}`;

	return (
		<>
			{/* Header — `pr-10` reserves space for HeroUI's absolutely-positioned
			    close button so the view-selector doesn't overlap it. Inline colors
			    force past Bootstrap's global anchor/`.text-primary` rules. */}
			<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
				<span style={{ color: 'hsl(var(--heroui-primary))' }}>
					{isMaintenance ? <Wrench className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
				</span>
				<span
					className="text-lg font-bold flex-1"
					style={{ color: 'hsl(var(--heroui-foreground))' }}
				>
					{typeLabel}
				</span>
				{effectiveOrgUnique && eventId && (
					<Dropdown placement="bottom-end">
						<DropdownTrigger>
							<Button
								size="sm"
								variant="bordered"
								endContent={<ChevronDown className="w-3.5 h-3.5" />}
								style={{ color: 'hsl(var(--heroui-default-700))', textDecoration: 'none' }}
							>
								Open in…
							</Button>
						</DropdownTrigger>
						<DropdownMenu aria-label="Open event in another view">
							{viewLinks.map(v => (
								<DropdownItem
									key={v.key}
									as="a"
									href={v.href}
									endContent={<ExternalLink className="w-3.5 h-3.5 opacity-60" />}
								>
									{v.label}
								</DropdownItem>
							))}
						</DropdownMenu>
					</Dropdown>
				)}
			</ModalHeader>

			{/* Body — two columns */}
			<ModalBody className="pt-5">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
					className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0"
				>
					{/* Left column */}
					<div>
						<Field label="Organization">{event.organizationName}</Field>

						<Field label="Location">
							{event.locationName ? (
								<LocationLabel
									name={event.locationName}
									timeZoneId={event.locationTimezoneId}
									date={event.startTime ? new Date(Number(event.startTime) * 1000) : undefined}
								/>
							) : null}
						</Field>

						<Field label="Status">
							<div className="flex items-center gap-3 flex-wrap">
								<Chip size="sm" variant="flat" color={status.color}>
									{status.label}
								</Chip>
								{/* Status-block actions — three-axis gate:
								    1. Lifecycle phase (`status.phase`).
								    2. `additionalData` enable flag from backend
								       (`checkOutEnable` / `checkInEnable` +
								       `aircraftExists` for checkout). Legacy parity
								       per `calendar.event-view.js:266-338`.
								    3. Per-user permission flag (Phase 2.2 Sub 3).
								    All three must pass → no 403 path. */}
								{status.phase === 'checkout' &&
									status.checkOutEnable &&
									status.aircraftExists &&
									f.canCheckOut && (
										<Button size="sm" color="primary" onPress={onCheckOutClick}>
											Check Out
										</Button>
									)}
								{status.phase === 'checkin' && status.checkInEnable && f.canCheckIn && (
									<Button size="sm" color="primary" onPress={onCheckInClick}>
										Check In
									</Button>
								)}
								{/* Check-out error chip — surfaces the backend's
								    message inline (e.g. "Aircraft is currently
								    grounded"). Legacy parity at
								    `calendar.event-view.js`. */}
								{status.checkOutError && (
									<Chip size="sm" variant="flat" color="danger" className="max-w-full">
										{status.checkOutError}
									</Chip>
								)}
								{/* Waiting-list position — only renders for events
								    that are queued. Legacy parity. Backend ships
								    `positionInQueue: 0` for non-waiting events; treat
								    0 as "not queued" rather than "first in line"
								    (`!= null` would otherwise render "Position in
								    queue: 0" for every regular event). */}
								{status.positionInQueue > 0 && (
									<Chip size="sm" variant="flat" color="default">
										Position in queue: {status.positionInQueue}
									</Chip>
								)}
							</div>
						</Field>

						<Field label="Pilot">{renderPerson(pilotFull, event.pilotId, pilotIsMe)}</Field>

						<Field label="SIC Pilot">
							{renderPerson(coPilotFull, event.coPilotId, coPilotIsMe)}
						</Field>

						<Field label="Instructor">
							<div className="flex items-center gap-2 flex-wrap">
								{renderPerson(instructorFull, event.instructorId, instructorIsMe)}
								{instructorTotal != null && (
									<span className="text-default-500" title="Instructor time logged at check-in">
										(Total: {instructorTotal})
									</span>
								)}
							</div>
						</Field>

						{customFieldRows.map(row => (
							<Field key={row.id} label={row.name}>
								{row.value != null ? (
									<span>{row.display}</span>
								) : (
									<span className="text-default-400">None</span>
								)}
							</Field>
						))}

						{!isMaintenance && (
							<Field label="Activity Type">
								{activityTypeEntry ? (
									<span className="inline-flex items-center gap-2">
										<span>{activityTypeEntry.name}</span>
										{activityTypeEntry.color && (
											<span
												className="inline-block w-3 h-3 rounded-full shrink-0"
												style={{ backgroundColor: activityTypeEntry.color }}
												aria-hidden="true"
											/>
										)}
									</span>
								) : (
									<span className="text-default-400">None</span>
								)}
							</Field>
						)}
					</div>

					{/* Right column */}
					<div>
						<Field label="Start Time">
							<p>{formatDateTime(event.startTime, eventTz)}</p>
							<p className="text-xs text-default-400">
								{formatDateTimeWithOffset(event.startTime, filterTz)}
							</p>
						</Field>

						<Field label="End Time">
							<p>{formatDateTime(event.finishTime, eventTz)}</p>
							<p className="text-xs text-default-400">
								{formatDateTimeWithOffset(event.finishTime, filterTz)}
							</p>
						</Field>

						{isRecurring && (
							<Field label="Recurring Event">
								<p className="text-sm">{formatRecurrenceSummary(event, eventTz) || 'Recurring'}</p>
							</Field>
						)}

						<Field label="Resources">
							{Array.isArray(event.eventResources) && event.eventResources.length > 0 ? (
								<div className="flex flex-col gap-1">
									{[...event.eventResources]
										.filter(er => {
											// Aviation resources (aircraft/simulator) always kept.
											if (pickAircraft(er) || pickSimulator(er)) return true;
											// Drop instructor entries — Symfony emits the instructor as
											// an eventResource (Event.php:1027). Instructor has its own
											// dedicated field in the modal, so leaking it here is a
											// duplicate. Defensive multi-shape detection covers known
											// serializations (nested `resource.user`, flat `resource.type`,
											// or `instructorId` marker).
											const resType = er.resource?.type;
											const typeName = typeof resType === 'object' ? resType?.name : resType;
											if (typeName && String(typeName).toLowerCase().includes('instructor'))
												return false;
											if (er.resource?.user || er.instructorId != null) return false;
											return true;
										})
										.sort((a, b) => {
											const aIsAviation = pickAircraft(a) || pickSimulator(a) ? 0 : 1;
											const bIsAviation = pickAircraft(b) || pickSimulator(b) ? 0 : 1;
											return aIsAviation - bIsAviation;
										})
										.map((er, i) => {
											const aircraft = pickAircraft(er);
											const simulator = pickSimulator(er);
											const isAviationResource = !!(aircraft || simulator);
											let type, entityId, label, typeDisplay;
											if (aircraft) {
												type = 'aircraft';
												entityId = aircraft.id;
												label =
													aircraft.registrationNumber ||
													aircraft.regNumb ||
													aircraft.name ||
													'Aircraft';
												typeDisplay = 'Aircraft';
											} else if (simulator) {
												type = 'simulator';
												entityId = simulator.id;
												label = simulator.name || 'Simulator';
												typeDisplay = 'Simulator';
											} else {
												// Non-aviation resource (Headphones, Classroom, etc.)
												// Symfony serializes name/type under er.resource.* — mirror EventFormBody pattern
												type = er.typeUnique || er.type || 'other';
												entityId = er.resourceID || er.resourceId || er.resource?.id || er.id;
												label = er.resource?.name || er.name || er.label || '—';
												const rawTypeName =
													er.typeName ||
													er.resource?.typeName ||
													(typeof er.resource?.type === 'object'
														? er.resource?.type?.name
														: er.resource?.type) ||
													null;
												typeDisplay = rawTypeName || (type !== 'other' ? type : 'Resource');
											}
											const canDrillIn =
												isAviationResource && !!perms?.canViewResource && entityId != null;
											const badge = type === 'aircraft' ? resourceBadges?.[entityId] : null;
											return (
												<div
													key={`${type}-${entityId ?? i}`}
													className="text-sm flex items-center gap-1 flex-wrap"
												>
													<strong className="text-default-700">{typeDisplay}:</strong>
													{canDrillIn ? (
														<button
															onClick={() => onResourceClick?.({ id: entityId, type, label })}
															className="hover:underline flex items-center gap-1"
															style={{ color: 'hsl(var(--heroui-primary))' }}
														>
															<Layers className="w-3.5 h-3.5 flex-shrink-0" />
															{label}
														</button>
													) : (
														<span className="text-default-700">{label}</span>
													)}
													{badge && badge.count > 0 && (
														<span className="text-xs text-default-500 font-normal">
															(Discrepancies: {badge.count})
														</span>
													)}
													{badge?.airworthyStatus === 'airworthy' && (
														<CheckCircle
															className="w-4 h-4 text-success flex-shrink-0"
															fill="currentColor"
															stroke="white"
															strokeWidth={2.5}
														/>
													)}
													{badge?.airworthyStatus === 'unairworthy' && (
														<XCircle
															className="w-4 h-4 text-danger flex-shrink-0"
															fill="currentColor"
															stroke="white"
															strokeWidth={2.5}
														/>
													)}
													{!isAviationResource && null}
												</div>
											);
										})}
								</div>
							) : /* Legacy flattened payload fallback */
							regNum ? (
								<span className="text-sm text-default-700">
									{event.simulator ? 'Simulator' : 'Aircraft'}: {regNum}
								</span>
							) : event.resourceName ? (
								<span className="text-sm text-default-700">{event.resourceName}</span>
							) : (
								<span className="text-default-400">None</span>
							)}
						</Field>

						{/* CalAudit-P0 Task:11 — gated by `f.canEdit`, the SAME per-event
						    edit permission the footer's Edit button uses (own/other/
						    maintenance compound check). Previously gated only by
						    `canManageWb` (a feature-capability flag, not a permission),
						    a view-only user could open this link and mutate someone
						    else's passengers/W&B. */}
						{canManageWb && !isMaintenance && onWbClick && f.canEdit && (
							<button
								type="button"
								onClick={onWbClick}
								className="text-sm text-left hover:underline self-start mt-1"
								style={{ color: 'hsl(var(--heroui-primary))' }}
							>
								Manage passengers ({seatsText}) &amp; Weight and Balance
							</button>
						)}

						{(event.comments || event.description) && (
							<Field label="Comments">{event.comments || event.description}</Field>
						)}

						{hasWb && (
							<Field label="Weight & Balance">
								<p className={wbOver ? 'text-danger font-semibold' : ''}>
									Available: {event.availableWeight} {weightUnit}
								</p>
								<p className="text-xs text-default-500">Passengers: {seatsText}</p>
							</Field>
						)}
					</div>
				</motion.div>
			</ModalBody>

			<ModalFooter className="justify-between gap-2 flex-wrap border-t border-default-200 pt-3">
				<div className="flex gap-2 flex-wrap">
					{/* Delete: `calendarEventDelete` (or `resourcesManage + maintenanceManage`
					    for maintenance). Hides on completed events. Hidden until
					    perms load → cross-org events don't briefly show a button
					    the user lacks permission for. */}
					{f.canDelete && (
						<Button size="sm" color="danger" onPress={onDeleteClick}>
							Delete
						</Button>
					)}
					{/* Cancel: legacy parity = `availableToManage && isAir && !eventCompleted`.
					    Phase 2.2 Sub 3 fix — previously gated on `canDelete`, which
					    blocked users with Cancel-only perms (e.g. `aartemorlov1998`
					    org73). Now reads `f.canCancel`. */}
					{f.canCancel && (
						<Button size="sm" color="warning" onPress={onCancelClick}>
							Cancel
						</Button>
					)}
					{/* Cancel Check Out: surfaces during the checkOutEnable=0 +
					    checkInEnable=1 lifecycle window. Legacy parity. In-modal
					    confirm + POST per Phase 2.1 Sub 14 (replaces the prior
					    `goTo('event-cancel-checkout')` redirect). */}
					{f.canCancelCheckOut && (
						<Button size="sm" color="warning" onPress={onCancelCheckOutClick}>
							Cancel Check Out
						</Button>
					)}
					{/* Cancel Check In: surfaces post-completion (revert path).
					    Phase 2.1 Sub 14 wired the in-modal confirm + POST. */}
					{f.canCancelCheckIn && (
						<Button size="sm" color="warning" onPress={onCancelCheckInClick}>
							Cancel Check In
						</Button>
					)}
				</div>
				<div className="flex gap-2 flex-wrap">
					{/* View Invoice: existing invoice → link to legacy invoice page.
					    Legacy `event-modal-footer.js:301-315` parity.
					    Rem20 Sub 7c: gated by `SHOW_INVOICE_AFFORDANCES` while
					    invoice flow is being rewritten. */}
					{SHOW_INVOICE_AFFORDANCES && f.canViewInvoice && (
						<Button size="sm" variant="flat" onPress={goTo('event-view-invoice')}>
							View Invoice
						</Button>
					)}
					{/* Create Invoice: air events, no existing invoice, gated by
					    `invoiceCreate` (own) or both perms (other). Legacy
					    `event-modal-footer.js:316-324` parity.
					    Rem20 Sub 7c: gated by `SHOW_INVOICE_AFFORDANCES` while
					    invoice flow is being rewritten. */}
					{SHOW_INVOICE_AFFORDANCES && f.canCreateInvoice && (
						<Button size="sm" variant="flat" onPress={goTo('event-create-invoice')}>
							Create Invoice
						</Button>
					)}
					{/* Add to Waiting List — `availableToManage` + air + aircraft + !completed.
					    Legacy `event-modal-footer.js:339-350`. Rem20 Sub 7b: backend
					    `EventAdditionalDataDto.aviationResourceExists` is now the
					    authoritative signal; fall back to a JSX scan of
					    `eventResources` only when the DTO flag is missing entirely. */}
					{f.canAddToWaiting &&
						(() => {
							const ad = event.additionalData || {};
							const dtoSaysAircraft =
								ad.aviationResourceExists === true ||
								ad.aircraftExists === true ||
								ad.aircraftExists === 1 ||
								ad.aircraftExists === '1' ||
								event.aviationResourceExists === true ||
								event.aircraftExists === true ||
								event.aircraftExists === 1 ||
								event.aircraftExists === '1';
							const resourceScan =
								Array.isArray(event.eventResources) &&
								event.eventResources.some(er => pickAircraft(er));
							if (!dtoSaysAircraft && !resourceScan) return null;
							return (
								<Button size="sm" color="primary" variant="flat" onPress={onWaitingListClick}>
									Add to Waiting List
								</Button>
							);
						})()}
					{/* Manage passengers & W&B link lives in the modal body under
					    Resources (see EventContent) — not in the footer. */}
					{/* Edit: per-button-gate map. Single source of truth for
					    air own/other + maintenance compound perm check. */}
					{f.canEdit && (
						<Button size="sm" color="primary" onPress={onEditClick}>
							Edit
						</Button>
					)}
					<Button size="sm" variant="bordered" onPress={onClose}>
						Close
					</Button>
				</div>
			</ModalFooter>
		</>
	);
}

export default EventViewModal;
